import * as env from 'config/environment';
import React, { useState } from 'react';
import { getUserStorage } from 'components/features/utils/utils';
import { isApp, isIosApp } from 'components/features/utils/userStatus';
import LoginSideMenu from './components/LoginSideMenu';
import { HeadBlock } from './components/HeadBlock';

export const fromLpToNoticeURL = 'notificationList/2/1/1';

type UserType = 'guest' | 'premium' | 'not_premium';

const HeroStickyCtaButtons: React.FC<{
  userType: UserType;
  login: () => void;
  minkabuLogin: () => void;
}> = ({ userType, login, minkabuLogin }) => (
  <div className="max-w-md mx-auto flex items-center gap-2 px-3 py-4">
    {userType === 'guest' ? (
      <>
        <button
          type="button"
          className="flex justify-center items-center h-12.5 px-4 text-base font-bold text-white rounded-lg bg-gradient-to-r bg-gradient-r-red cursor-pointer hover:opacity-75"
          style={{ flex: '7 1 0%' }}
          onClick={(): void => login()}>
          新規で始める
        </button>
        <button
          type="button"
          className="flex justify-center items-center h-12.5 px-4 text-base font-bold text-bluegray-600 bg-bluegray-50 border border-bluegray-400 rounded-lg cursor-pointer hover:opacity-75"
          style={{ flex: '3 1 0%' }}
          onClick={(): void => minkabuLogin()}>
          ログイン
        </button>
      </>
    ) : (
      <button
        type="button"
        className="w-full flex justify-center items-center h-12.5 text-base font-bold text-white rounded-lg bg-gradient-to-r bg-gradient-r-red cursor-pointer hover:opacity-75"
        onClick={(): void => login()}>
        資産をチャートで見てみる
      </button>
    )}
  </div>
);

const HeroStickyCta: React.FC<{
  userType: UserType;
  login: () => void;
  minkabuLogin: () => void;
}> = ({ userType, login, minkabuLogin }) => (
  <>
    {/* SP: フッターナビ（高さ60px）に追従 */}
    <div className="lg:hidden sticky left-0 w-full bottom-60p z-40 bg-white border-b border-bluegray-200">
      <HeroStickyCtaButtons
        userType={userType}
        login={login}
        minkabuLogin={minkabuLogin}
      />
    </div>
    {/* PC: フッターに追従 */}
    <div className="hidden lg:block sticky left-0 w-full bottom-0 z-40 bg-white border-b border-bluegray-200">
      <HeroStickyCtaButtons
        userType={userType}
        login={login}
        minkabuLogin={minkabuLogin}
      />
    </div>
  </>
);

const TemporaryLogin: React.FC = () => {
  const [isShowSideMenu, setIsShowSideMenu] = useState(false);
  const [hideAnimate, setHideAnimate] = useState<boolean>(false);

  const menuShow = (): void => {
    setIsShowSideMenu(true);
    setHideAnimate(false);
  };
  const menuHide = (): void => {
    setIsShowSideMenu(false);
  };
  const menuHideAnimate = (): void => {
    setHideAnimate(true);
  };

  const goBack = () => {
    window.history.back();
  };

  const linkGuest = `${env.registerURL}?utm_source=tw_minkabu&utm_medium=temporarylogin&utm_campaign=guest_asset_202609&service=TradeWell&callback_url=${env.appURL}/auth`;
  const linkNotPremium = `${env.premiumURL}?utm_source=tw_minkabu&utm_medium=temporarylogin&utm_campaign=free_asset_202609&service=TradeWell&callback_url=${env.appURL}/auth`;
  const linkPremium = `${env.appURL}/auth`;

  const userStorage = getUserStorage();
  const userType =
    userStorage === null
      ? 'guest'
      : userStorage.Charged
      ? 'premium'
      : 'not_premium';

  const login = () => {
    const link =
      userType === 'guest'
        ? linkGuest
        : userType === 'premium'
        ? linkPremium
        : linkNotPremium;
    window.location.href = link;
  };

  const minkabuLogin = (): void => {
    window.location.href = 'https://id.minkabu.jp/login';
  };

  return (
    <div className="relative">
      <HeadBlock {...{ noindex: true }} />
      {/* ヘッダー */}
      <header className="bg-white z-50 shadow-md sticky top-0" id="id_header">
        <div className="ui-header-sp border-b border-gray-300">
          <nav className="flex p-2">
            <div
              className="flex-1 flex justify-start"
              onClick={(): void => goBack()}>
              <button className="flex items-center p-2">
                <i className="icon-chevron-left text-2xl text-bluegray-500"></i>
                <span className="text-gray-800">戻る</span>
              </button>
            </div>
            <div className="flex-grow flex justify-center items-center">
              <a title="みんかぶ" href="https://minkabu.jp/">
                <img
                  src="https://assets.minkabu.jp/images/logo/v4/logo_minkabu.svg"
                  width="110"
                  height="30"
                  alt="みんかぶ"
                />
              </a>
            </div>
            <div className="flex-1 flex justify-end">
              <section className="pl-4 pr-2 pt-2">
                {!isApp && (
                  <div
                    className={`btn-trigger ${isShowSideMenu ? 'active' : ''}`}
                    id="btn01"
                    onClick={!isShowSideMenu ? menuShow : menuHideAnimate}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                )}
              </section>
            </div>
          </nav>
        </div>
      </header>
      {isShowSideMenu && (
        <div className={`fixed left-0 ui-h-screen w-screen z-20`}>
          <LoginSideMenu
            hideAnimate={hideAnimate}
            menuHide={menuHide}
            onNavLinkClick={(e: React.MouseEvent, path: string) => {
              return;
            }}
          />
        </div>
      )}
      {/* コンテンツ */}
      <div className="login relative">
        {/* pc */}
        <div className="lg:flex hidden flex-col relative">
          <div
            className="absolute inset-0 bg-slate-800"
            style={{ height: '165px' }}
            aria-hidden="true"
          />
          <div className="relative mx-auto">
            <img
              className="w-full h-auto"
              src="/static/images/temporary_login/pc_top.svg"
              alt=""
            />
          </div>
        </div>
        {/* sp */}
        <div className="w-full lg:hidden flex flex-col text-white bg-gray-800">
          <div className="max-w-md mx-auto flex flex-col items-center">
            <div className="relative flex justify-center text-center w-full h-full">
              <video
                className="w-full max-w-[375px]"
                src="/static/images/temporary_login/sp_footer_video.mp4"
                autoPlay
                loop
                muted
                playsInline
              />
            </div>
          </div>
        </div>
        {/* みんかぶアセットプランナーでできること */}
        <div className="w-full bg-white">
          <div className="flex flex-col pt-8 lg:mb-16 text-xl font-bold items-center w-full">
            <div>みんかぶアセットプランナーで</div>
            <div className="flex flex-row">
              できること
              <img
                src="/static/images/temporary_login/light.svg"
                alt="チャート"
                className=""
              />
            </div>
          </div>
          {/* PC */}
          <div className="hidden lg:flex flex-col pb-8">
            <div
              className="flex flex-row items-center justify-between mx-auto"
              style={{ width: '750px', height: '300px' }}>
              <div
                className="flex flex-col w-full h-full pt-6 pb-4 px-4 bg-white border border-bluegray-300 rounded-lg shadow"
                style={{ width: '350px' }}>
                <div className="relative -top-6">
                  <h3 className="flex items-center mb-6 absolute bubble-border-block w-full lg:max-w-bubble lg:bubble-border-block">
                    <img
                      src="/static/images/temporary_login/what_is_possible1_title.svg"
                      alt="複数の証券口座をまとめて管理"
                      style={{ width: '315px', height: '70px' }}
                    />
                  </h3>
                </div>
                <img
                  src="/static/images/temporary_login/what_is_possible1.svg"
                  alt="複数の証券口座をまとめて管理"
                  className="w-full"
                />
                <div className="flex flex-row items-baseline mt-4">
                  <div className="icon-check text-cyan-600 text-lg font-bold"></div>
                  <div className="ml-1">
                    複数の証券口座にある株式、投資信託をまとめて管理できます。
                    <br />
                    資産推移や損益をチャートやグラフで確認できます。NISA、iDeCo対応。
                  </div>
                </div>
              </div>
              <div
                className="flex flex-col w-full h-full pt-6 pb-4 px-4 bg-white border border-bluegray-300 rounded-lg shadow"
                style={{ width: '350px' }}>
                <div className="relative -top-6">
                  <h3 className="flex items-center mb-6 absolute bubble-border-block w-full lg:max-w-bubble lg:bubble-border-block">
                    <img
                      src="/static/images/temporary_login/what_is_possible6_title.svg"
                      alt="配当金を支払い月ごとに管理"
                      style={{ width: '315px', height: '70px' }}
                    />
                  </h3>
                </div>
                <img
                  src="/static/images/temporary_login/what_is_possible6.svg"
                  alt="配当金を支払い月ごとに管理"
                  className="w-full"
                />
                <div className="flex flex-row items-baseline mt-4">
                  <div className="icon-check text-cyan-600 text-lg font-bold"></div>
                  <div className="ml-1">
                    毎月もらえる配当金額を自動で計算し支払い月ごとに一覧で表示します。1年間の合計額や保有銘柄の内訳もわかります。
                  </div>
                </div>
              </div>
            </div>
            <div
              className="flex flex-row items-center justify-between mx-auto mt-18"
              style={{ width: '750px', height: '300px' }}>
              <div
                className="flex flex-col w-full h-full pt-6 pb-4 px-4 bg-white border border-bluegray-300 rounded-lg shadow"
                style={{ width: '350px' }}>
                <div className="relative -top-6">
                  <h3 className="flex items-center mb-6 absolute bubble-border-block w-full lg:max-w-bubble lg:bubble-border-block">
                    <img
                      src="/static/images/temporary_login/what_is_possible5_title.svg"
                      alt="ライフプランに合わせた資産形成"
                      style={{ width: '315px', height: '70px' }}
                    />
                  </h3>
                </div>
                <img
                  src="/static/images/temporary_login/what_is_possible5.svg"
                  alt="ライフプランに合わせた資産形成"
                  className="w-full"
                />
                <div className="flex flex-row items-baseline mt-4">
                  <div className="icon-check text-cyan-600 text-lg font-bold"></div>
                  <div className="ml-1">
                    老後資金など「お金を貯める目的ごと」に資産運用をシミュレーションできます。低リスク運用をAIがアドバイスします。
                  </div>
                </div>
              </div>
              <div
                className="flex flex-col w-full h-full pt-6 pb-4 px-4 bg-white border border-bluegray-300 rounded-lg shadow"
                style={{ width: '350px' }}>
                <div className="relative -top-6">
                  <h3 className="flex items-center mb-6 absolute bubble-border-block w-full lg:max-w-bubble lg:bubble-border-block">
                    <img
                      src="/static/images/temporary_login/what_is_possible3_title.svg"
                      alt="取引履歴の保存・分析"
                      style={{ width: '315px', height: '70px' }}
                    />
                  </h3>
                </div>
                <img
                  src="/static/images/temporary_login/what_is_possible3.svg"
                  alt="取引履歴の保存・分析"
                  className="w-full"
                />
                <div className="flex flex-row items-baseline mt-4">
                  <div className="icon-check text-cyan-600 text-lg font-bold"></div>
                  <div className="ml-1">
                    取引履歴を株価チャートにプロットし、過去の売買タイミングから投資の傾向がわかります。
                  </div>
                </div>
              </div>
            </div>
            <div
              className="flex flex-row items-center justify-between mx-auto mt-18"
              style={{ width: '750px', height: '300px' }}>
              <div
                className="flex flex-col w-full h-full pt-6 pb-4 px-4 bg-white border border-bluegray-300 rounded-lg shadow"
                style={{ width: '350px' }}>
                <div className="relative -top-6">
                  <h3 className="flex items-center mb-6 absolute bubble-border-block w-full lg:max-w-bubble lg:bubble-border-block">
                    <img
                      src="/static/images/temporary_login/what_is_possible2_title.svg"
                      alt="株式の資産推移をグラフ化"
                      style={{ width: '315px', height: '70px' }}
                    />
                  </h3>
                </div>
                <img
                  src="/static/images/temporary_login/what_is_possible2.svg"
                  alt="株式の資産推移をグラフ化"
                  className="w-full"
                />
                <div className="flex flex-row items-baseline mt-4">
                  <div className="icon-check text-cyan-600 text-lg font-bold"></div>
                  <div className="ml-1">
                    株式、投資信託の資産推移や損益をチャートやグラフで確認できます。
                  </div>
                </div>
              </div>
              <div
                className="flex flex-col w-full h-full pt-6 pb-4 px-4 bg-white border border-bluegray-300 rounded-lg shadow"
                style={{ width: '350px' }}>
                <div className="relative -top-6">
                  <h3 className="flex items-center mb-6 absolute bubble-border-block w-full lg:max-w-bubble lg:bubble-border-block">
                    <img
                      src="/static/images/temporary_login/what_is_possible4_title.svg"
                      alt="LINEで投資の損益管理"
                      style={{ width: '315px', height: '70px' }}
                    />
                  </h3>
                </div>
                <img
                  src="/static/images/temporary_login/what_is_possible4.svg"
                  alt="LINEで投資の損益管理"
                  className="w-full"
                />
                <div className="flex flex-row items-baseline mt-4">
                  <div className="icon-check text-cyan-600 text-lg font-bold"></div>
                  <div className="ml-1">
                    日ごと、週ごと、月ごとに投資の損益や資産変動をお知らせします。
                    <br />
                    LINE通知はアセプラだけ！
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* sp */}
          <div className="lg:hidden flex flex-col pb-8">
            <div className="flex flex-col items-center px-2">
              <div
                className="flex flex-col h-full mt-16 pt-6 pb-4 px-4 bg-white border border-bluegray-300 rounded-lg shadow"
                style={{ width: '360px' }}>
                <div className="relative -top-6">
                  <h3 className="flex items-center justify-center mb-6 absolute bubble-border-block w-full lg:max-w-bubble lg:bubble-border-block">
                    <img
                      src="/static/images/temporary_login/what_is_possible1_title.svg"
                      alt="複数の証券口座をまとめて管理"
                    />
                  </h3>
                </div>
                <div className="flex justify-center">
                  <img
                    src="/static/images/temporary_login/what_is_possible1.svg"
                    alt="複数の証券口座をまとめて管理"
                  />
                </div>
                <div className="flex flex-row items-baseline mt-4">
                  <div className="icon-check text-cyan-600 text-lg font-bold"></div>
                  <div className="ml-1">
                    複数の証券口座にある株式、投資信託をまとめて管理できます。
                    <br />
                    資産推移や損益をチャートやグラフで確認できます。NISA、iDeCo対応。
                  </div>
                </div>
              </div>
              <div
                className="flex flex-col h-full mt-16 pt-6 pb-4 px-4 bg-white border border-bluegray-300 rounded-lg shadow"
                style={{ width: '360px' }}>
                <div className="relative -top-6">
                  <h3 className="flex items-center justify-center mb-6 absolute bubble-border-block w-full lg:max-w-bubble lg:bubble-border-block">
                    <img
                      src="/static/images/temporary_login/what_is_possible6_title.svg"
                      alt="配当金を支払い月ごとに管理"
                    />
                  </h3>
                </div>
                <div className="flex justify-center">
                  <img
                    src="/static/images/temporary_login/what_is_possible6.svg"
                    alt="配当金を支払い月ごとに管理"
                  />
                </div>
                <div className="flex flex-row items-baseline mt-4">
                  <div className="icon-check text-cyan-600 text-lg font-bold"></div>
                  <div className="ml-1">
                    毎月もらえる配当金額を自動で計算し支払い月ごとに一覧で表示します。1年間の合計額や保有銘柄の内訳もわかります。
                  </div>
                </div>
              </div>
              <div
                className="flex flex-col h-full mt-16 pt-6 pb-4 px-4 bg-white border border-bluegray-300 rounded-lg shadow"
                style={{ width: '360px' }}>
                <div className="relative -top-6">
                  <h3 className="flex items-center justify-center mb-6 absolute bubble-border-block w-full lg:max-w-bubble lg:bubble-border-block">
                    <img
                      src="/static/images/temporary_login/what_is_possible5_title.svg"
                      alt="ライフプランに合わせた資産形成"
                    />
                  </h3>
                </div>
                <div className="flex justify-center">
                  <img
                    src="/static/images/temporary_login/what_is_possible5.svg"
                    alt="ライフプランに合わせた資産形成"
                  />
                </div>
                <div className="flex flex-row items-baseline mt-4">
                  <div className="icon-check text-cyan-600 text-lg font-bold"></div>
                  <div className="ml-1">
                    老後資金など「お金を貯める目的ごと」に資産運用をシミュレーションできます。低リスク運用をAIがアドバイスします。
                  </div>
                </div>
              </div>
              <div
                className="flex flex-col h-full mt-16 pt-6 pb-4 px-4 bg-white border border-bluegray-300 rounded-lg shadow"
                style={{ width: '360px' }}>
                <div className="relative -top-6">
                  <h3 className="flex items-center justify-center mb-6 absolute bubble-border-block w-full lg:max-w-bubble lg:bubble-border-block">
                    <img
                      src="/static/images/temporary_login/what_is_possible3_title.svg"
                      alt="取引履歴の保存・分析"
                    />
                  </h3>
                </div>
                <div className="flex justify-center">
                  <img
                    src="/static/images/temporary_login/what_is_possible3.svg"
                    alt="取引履歴の保存・分析"
                  />
                </div>
                <div className="flex flex-row items-baseline mt-4">
                  <div className="icon-check text-cyan-600 text-lg font-bold"></div>
                  <div className="ml-1">
                    取引履歴を株価チャートにプロットし、過去の売買タイミングから投資の傾向がわかります。
                  </div>
                </div>
              </div>
              <div
                className="flex flex-col h-full mt-16 pt-6 pb-4 px-4 bg-white border border-bluegray-300 rounded-lg shadow"
                style={{ width: '360px' }}>
                <div className="relative -top-6">
                  <h3 className="flex items-center justify-center mb-6 absolute bubble-border-block w-full lg:max-w-bubble lg:bubble-border-block">
                    <img
                      src="/static/images/temporary_login/what_is_possible2_title.svg"
                      alt="株式の資産推移をグラフ化"
                    />
                  </h3>
                </div>
                <div className="flex justify-center">
                  <img
                    src="/static/images/temporary_login/what_is_possible2.svg"
                    alt="株式の資産推移をグラフ化"
                  />
                </div>
                <div className="flex flex-row items-baseline mt-4">
                  <div className="icon-check text-cyan-600 text-lg font-bold"></div>
                  <div className="ml-1">
                    株式、投資信託の資産推移や損益をチャートやグラフで確認できます。
                  </div>
                </div>
              </div>
              <div
                className="flex flex-col h-full mt-16 pt-6 pb-4 px-4 bg-white border border-bluegray-300 rounded-lg shadow"
                style={{ width: '360px' }}>
                <div className="relative -top-6">
                  <h3 className="flex items-center justify-center mb-6 absolute bubble-border-block w-full lg:max-w-bubble lg:bubble-border-block">
                    <img
                      src="/static/images/temporary_login/what_is_possible4_title.svg"
                      alt="LINEで投資の損益管理"
                    />
                  </h3>
                </div>
                <div className="flex justify-center">
                  <img
                    src="/static/images/temporary_login/what_is_possible4.svg"
                    alt="LINEで投資の損益管理"
                  />
                </div>
                <div className="flex flex-row items-baseline mt-4">
                  <div className="icon-check text-cyan-600 text-lg font-bold"></div>
                  <div className="ml-1">
                    日ごと、週ごと、月ごとに投資の損益や資産変動をお知らせします。
                    <br />
                    LINE通知はアセプラだけ！
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* フッター */}
      <div
        id="footer"
        className="w-full bg-gray-800 py-4 px-2 text-white text-sm">
        <ul className="text-center mb-8">
          <li className="inline-block mr-4">
            <a
              href="https://minkabu.co.jp/"
              target="_blank"
              rel="noopener noreferrer">
              運営会社
            </a>
          </li>
          <li className="inline-block mr-4">
            <a
              href="https://info.minkabu.jp/tw_terms/"
              target="_blank"
              rel="noopener noreferrer">
              利用規約
            </a>
          </li>
          <li className="inline-block mr-4">
            <a
              href="https://minkabu.co.jp/privacy_policy"
              target="_blank"
              rel="noopener noreferrer">
              プライバシーポリシー
            </a>
          </li>
          {!isIosApp() && (
            <>
              <li className="inline-block mr-4">
                <a
                  href="https://info.minkabu.jp/economy/"
                  target="_blank"
                  rel="noopener noreferrer">
                  特定商取引表示
                </a>
              </li>
              <li className="inline-block mr-4">
                <a
                  href="https://info.minkabu.jp/law/"
                  target="_blank"
                  rel="noopener noreferrer">
                  ご利用上の注意（関連法規）
                </a>
              </li>
              <li className="inline-block mr-4">
                <a
                  href="https://info.minkabu.jp/ad/"
                  target="_blank"
                  rel="noopener noreferrer">
                  広告掲載・取扱商品
                </a>
              </li>
              <li className="inline-block mr-4">
                <a
                  href="https://support.minkabu.jp/hc/ja"
                  target="_blank"
                  rel="noopener noreferrer">
                  ヘルプ/FAQ
                </a>
              </li>
            </>
          )}
        </ul>
        <div className="mb-4">
          <a
            href="https://minkabu.co.jp/"
            className="text-center"
            title="株式会社ミンカブ・ジ・インフォノイドコーポレートサイトへ"
            target="_blank"
            rel="noopener noreferrer">
            <img
              loading="lazy"
              src="/static/images/lp/min_infonoid_logo_white.svg"
              width="150"
              height="25"
              className="mx-auto"
              alt="ミンカブ・ジ・インフォノイド ロゴ"
            />
          </a>
          <div className="text-center">
            <span>(C) MINKABU THE INFONOID, Inc.</span>
          </div>
        </div>
      </div>
      {/* SP: 固定表示の新規で始める/ログインボタンにフッターが隠れないよう確保するスペーサー */}
      <div className="lg:hidden h-60p" aria-hidden="true" />
      {/* PC: 固定表示の新規で始める/ログインボタンにフッターが隠れないよう確保するスペーサー */}
      <div className="hidden lg:block h-60p" aria-hidden="true" />
      {/* 動画下の追従CTA（PC/SP共通・.login内で最後までスティッキーに追従） */}
      <HeroStickyCta
        userType={userType}
        login={login}
        minkabuLogin={minkabuLogin}
      />
      {/* みんかぶHPフッター */}
      <nav className="w-full sticky bottom-0 left-0 z-10 bg-white shadow lg:hidden">
        <ul className="h-60p flex justify-between text-gray-dark">
          <li className="w-full">
            <a
              href="https://s.minkabu.jp"
              className="flex flex-col items-center justify-center h-full w-full">
              <img
                src="/static/images/icon/icon_home.svg"
                width="25"
                height="25"
                alt="TOP"
              />
              <p className="text-cente text-1.5xs">TOP</p>
            </a>
          </li>
          <li className="w-full">
            <a
              href="https://s.minkabu.jp/stock"
              className="flex flex-col items-center justify-center h-full w-full">
              <img
                src="/static/images/icon/icon_search.svg"
                width="25"
                height="25"
                alt="検索"
              />
              <p className="text-center text-1.5xs">検索</p>
            </a>
          </li>
          <li className="w-full">
            <a
              href="https://s.minkabu.jp/mypage/favorites"
              className="flex flex-col items-center justify-center h-full w-full">
              <img
                src="/static/images/icon/icon_heart_bold.svg"
                width="25"
                height="25"
                alt="お気に入り"
              />
              <p className="text-center text-1.5xs">お気に入り</p>
            </a>
          </li>
          <li className="w-full">
            <a
              href="/temporarylogin"
              className="flex flex-col items-center justify-center h-full w-full">
              <img
                src="/static/images/icon/icon_coin_many.svg"
                width="25"
                height="25"
                alt="資産管理"
              />
              <p className="text-center text-1.5xs">資産管理</p>
            </a>
          </li>
          <li className="w-full">
            <a
              href="https://s.minkabu.jp/mypage"
              className="flex flex-col items-center justify-center h-full w-full">
              <img
                src="/static/images/icon/icon_user.svg"
                width="25"
                height="25"
                alt="マイページ"
              />
              <p className="text-center text-1.5xs">マイページ</p>
            </a>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default TemporaryLogin;
